import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Send, ExternalLink, Copy, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageHeader, StatusBadge, Empty } from "@/components/Bits";
import { OutlookBanner } from "@/components/OutlookConnect";

const TABS = [["draft", "Review in-app"], ["pushed", "In Outlook"], ["sent", "Sent"], ["replied", "Replied"], ["dead", "Dead"], ["", "All"]];

function Editor({ d, onClose, onSaved, ai }) {
  const [subject, setSubject] = useState(d.subject);
  const [body, setBody] = useState(d.body);
  const [subs, setSubs] = useState([]);
  const editable = d.status === "draft";
  const call = async (fn, msg) => { try { const r = await fn(); onSaved(r.data); msg && toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const save = () => call(() => api.put(`/drafts/${d.id}`, { subject, body }), "Draft saved");
  const push = async () => { await api.put(`/drafts/${d.id}`, { subject, body }).catch(() => {}); call(() => api.post(`/drafts/${d.id}/push`), "Draft created in Outlook"); };
  const mark = (state) => call(() => api.post(`/drafts/${d.id}/mark`, { state }), `Marked ${state}`);
  const improve = async () => { try { const { data } = await api.post(`/drafts/${d.id}/ai-improve`); setBody(data.suggestion); toast.success("AI suggestion loaded — review before saving"); } catch (e) { toast.error(errMsg(e)); } };
  const suggest = async () => { try { const { data } = await api.post(`/drafts/${d.id}/ai-subjects`); setSubs(data.suggestions); } catch (e) { toast.error(errMsg(e)); } };
  const discard = async () => { try { await api.delete(`/drafts/${d.id}`); toast.success("Draft discarded — lead back to approved"); onSaved(null); window.dispatchEvent(new Event("kh-quota")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="draft-editor">
        <DialogHeader>
          <div className="kh-label">{d.track_name} · <StatusBadge status={d.status} /></div>
          <DialogTitle className="font-display text-xl">{d.lead_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">To: <span className="font-mono text-foreground" data-testid="draft-to">{d.to}</span></div>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!editable} data-testid="draft-subject-input" />
          {subs.length > 0 && <div className="flex flex-wrap gap-1.5">{subs.map((s) => <button key={s} onClick={() => setSubject(s)} className="rounded-full border px-3 py-1 text-xs hover:border-primary">{s}</button>)}</div>}
          <Textarea rows={13} value={body} onChange={(e) => setBody(e.target.value)} disabled={!editable} className="font-[450] leading-relaxed" data-testid="draft-body-input" />
          {editable && ai && (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={improve} data-testid="draft-ai-improve-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />Improve with AI</Button>
              <Button size="sm" variant="ghost" onClick={suggest} data-testid="draft-ai-subjects-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />Subject ideas</Button>
            </div>
          )}
        </div>
        {d.followups?.length > 0 && (
          <div className="space-y-2 border-t pt-3" data-testid="draft-followups">
            <div className="kh-label">Follow-ups</div>
            {d.followups.map((f) => (
              <div key={f.n} className="rounded-lg border p-3 text-sm">
                <div className="flex items-center justify-between"><span className="font-medium">#{f.n} · {f.subject}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${f.subject}\n\n${f.body}`); toast.success("Copied"); }} data-testid={`followup-copy-${f.n}`}><Copy className="h-3.5 w-3.5" /></Button>
                    {f.web_link && <a href={f.web_link} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></a>}
                  </div>
                </div>
                <div className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{f.body}</div>
                {f.outlook_error && <div className="mt-1 text-xs text-red-700">Outlook: {f.outlook_error}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 border-t pt-4">
          {editable && <Button onClick={push} data-testid="draft-push-btn"><Send className="mr-1 h-4 w-4" />Create Outlook draft</Button>}
          {editable && <Button variant="secondary" onClick={save} data-testid="draft-save-btn">Save</Button>}
          <Button variant="ghost" onClick={() => { navigator.clipboard.writeText(`${subject}\n\n${body}`); toast.success("Copied"); }} data-testid="draft-copy-btn"><Copy className="mr-1 h-4 w-4" />Copy</Button>
          {d.web_link && <a href={d.web_link} target="_blank" rel="noreferrer" data-testid="draft-open-outlook-link"><Button variant="ghost"><ExternalLink className="mr-1 h-4 w-4" />Open in Outlook</Button></a>}
          {["draft", "pushed"].includes(d.status) && <Button variant="ghost" onClick={() => mark("sent")} data-testid="draft-mark-sent-btn">Mark as sent</Button>}
          {["pushed", "sent"].includes(d.status) && <Button variant="ghost" onClick={() => mark("replied")} data-testid="draft-mark-replied-btn">Mark replied</Button>}
          {["pushed", "sent"].includes(d.status) && <Button variant="ghost" className="text-muted-foreground" onClick={() => mark("dead")} data-testid="draft-mark-dead-btn">Mark dead</Button>}
          {editable && <Button variant="ghost" className="ml-auto text-red-700" onClick={discard} data-testid="draft-discard-btn">Discard</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Drafts() {
  const [tab, setTab] = useState("draft");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [ol, setOl] = useState(null);
  const [ai, setAi] = useState(false);
  const load = () => api.get("/drafts", { params: { status: tab } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/outlook/status").then((r) => setOl(r.data)); api.get("/settings").then((r) => setAi(r.data.ai.enabled)); }, []);

  const pushAll = async () => { try { const { data } = await api.post("/drafts/push-all"); toast.success(`${data.pushed} pushed to Outlook`); data.errors.slice(0, 2).forEach((x) => toast.error(x.error)); load(); } catch (e) { toast.error(errMsg(e)); } };
  const sync = async () => { try { const { data } = await api.post("/drafts/sync"); toast.success(`Checked ${data.checked} · ${data.newly_sent} sent · ${data.new_replies} replies`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <>
      <PageHeader eyebrow="One draft per approved lead · you send manually" title="Draft Queue / Outbox">
        <span className={`rounded-full border px-3 py-1 text-xs ${ol?.connected ? "border-emerald-500/30 text-emerald-700" : "text-muted-foreground"}`} data-testid="outlook-status-pill">
          Outlook: {ol?.connected ? ol.mailbox : "not connected"}
        </span>
        <Button variant="outline" onClick={sync} disabled={!ol?.connected} data-testid="sync-outlook-btn"><RefreshCw className="mr-1 h-4 w-4" />Sync replies</Button>
        <Button onClick={pushAll} disabled={!ol?.connected} data-testid="push-all-btn"><Send className="mr-1 h-4 w-4" />Push all to Outlook</Button>
      </PageHeader>
      <OutlookBanner />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap bg-secondary/60">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`drafts-tab-${v || "all"}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      {rows.length === 0 ? <Empty testid="drafts-empty">Nothing here yet.</Empty> : (
        <div className="kh-card divide-y" data-testid="drafts-list">
          {rows.map((d) => (
            <button key={d.id} onClick={() => setEdit(d)} data-testid={`draft-row-${d.id}`} className="grid w-full grid-cols-[1.2fr_2fr_auto_auto] items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-accent/40">
              <div><div className="font-medium">{d.lead_name}</div><div className="text-xs text-muted-foreground">{d.track_name} · {d.to}</div></div>
              <div className="truncate text-sm text-muted-foreground">{d.subject}</div>
              <div className="text-xs text-muted-foreground">{fmtDateTime(d.sent_at || d.pushed_at || d.created_at)}</div>
              <StatusBadge status={d.status} />
            </button>
          ))}
        </div>
      )}
      {edit && <Editor d={edit} ai={ai} onClose={() => setEdit(null)} onSaved={(x) => { setEdit(x); load(); }} />}
    </>
  );
}
